import { FormEvent, type Ref, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"

import { ApiTypes } from "types"

import getErrorMessage from "utils/getErrorMessage"
import { Spinner } from "components/common/Spinner"
import { ButtonNew } from "components/form/ButtonNew"

import { PartnerCategoryRow } from "./PartnerCategoryRow"
import {
  buildPartnerFormData,
  buildVisibleCategoryIndex,
  computePartnerFormErrors,
  defaultPartnerValues,
  emptyPartnerRowTemplate,
  foldPartnerMutationResults,
  hydratePartnerMapsFromList,
  isPartnerDirty,
  mergeFieldErrorsForBlur,
  PhoneErrors,
  phoneErrorsNonEmpty,
  UrlErrors,
  urlErrorsNonEmpty,
  withHttpsIfSchemeless,
} from "./partnersFormUtils"
import { Footer, PageRoot, ScrollSection, SpinnerWrap, Title, Wrapper } from "./styled"
import type { ContractorPartnersFormApi, PartnerPayloadSnapshot, PartnerValues } from "./types"

const NO_PHONE_ERRORS: PhoneErrors = {}
const NO_URL_ERRORS: UrlErrors = {}

export type PartnerPersistResult = { ok: true } | { ok: false; reason: "validation" | "error"; error?: unknown }

export type ContractorPartnersFormHandle = {
  validatePartners: () => boolean
  hasChanges: () => boolean
  savePartners: (contractorId?: number) => Promise<PartnerPersistResult>
}

export type ContractorPartnersFormProps = {
  ref?: Ref<ContractorPartnersFormHandle>
  title?: string
  categories: Array<{ id: number; name: string }>
  excludeFirstCategory?: boolean
  isLoading: boolean
  initialPartners: ApiTypes.Model.ContractorPartnerProfileItem[]
  isSaving: boolean
  api?: ContractorPartnersFormApi
  variant?: "standalone" | "embedded"
  silentPartnerToasts?: boolean
  resolvePartnerApi?: (contractorId: number) => ContractorPartnersFormApi
  onDirtyChange?: (isDirty: boolean) => void
  messages?: {
    saveSuccess?: string
    saveError?: string
    noChanges?: string
  }
}

export function ContractorPartnersForm({
  ref,
  title = "Partners",
  categories,
  excludeFirstCategory = true,
  isLoading,
  initialPartners,
  isSaving,
  api,
  variant = "standalone",
  silentPartnerToasts: silentPartnerToastsProp,
  resolvePartnerApi,
  messages = {},
  onDirtyChange,
}: ContractorPartnersFormProps) {
  const silentPartnerToasts = silentPartnerToastsProp ?? variant === "embedded"

  const {
    saveSuccess = "Partners saved successfully",
    saveError = "Failed to save partners",
    noChanges = "No changes to save",
  } = messages

  const [checkedRows, setCheckedRows] = useState<Record<string, boolean>>({})
  const checkedRowsRef = useRef(checkedRows)
  checkedRowsRef.current = checkedRows

  const [accordionBodyMounted, setAccordionBodyMounted] = useState<Record<string, boolean>>({})
  const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>({})
  const scrollSectionRef = useRef<HTMLDivElement>(null)

  const visibleCategories = useMemo(
    () => (excludeFirstCategory ? categories.filter((_, index) => index !== 0) : categories),
    [categories, excludeFirstCategory],
  )

  const {
    keys: visiblePartnerKeys,
    keySet: categoryKeySet,
    categoryByKey: categoryByPartnerKey,
  } = useMemo(() => buildVisibleCategoryIndex(visibleCategories), [visibleCategories])

  const setOnlyAccordionOpen = (key: string) => {
    setOpenAccordions(prev => {
      const next: Record<string, boolean> = {}
      Object.keys(prev).forEach(k => {
        next[k] = false
      })
      next[key] = true
      return next
    })
  }

  const [partnerValues, setPartnerValues] = useState<Record<string, PartnerValues>>({})
  const [partnerRecordIdByKey, setPartnerRecordIdByKey] = useState<Record<string, number>>({})
  const [partnerLogoUrlByKey, setPartnerLogoUrlByKey] = useState<Record<string, string>>({})
  const [formUiReady, setFormUiReady] = useState(false)
  const hydratedFromServer = useRef(false)
  const savedSnapshotByKeyRef = useRef<Record<string, PartnerPayloadSnapshot>>({})
  const [logoErrors, setLogoErrors] = useState<Record<string, string>>({})
  const [urlErrors, setUrlErrors] = useState<Record<string, UrlErrors>>({})
  const [phoneErrors, setPhoneErrors] = useState<Record<string, PhoneErrors>>({})

  const partnerValuesRef = useRef(partnerValues)
  partnerValuesRef.current = partnerValues
  const partnerRecordIdByKeyRef = useRef(partnerRecordIdByKey)
  partnerRecordIdByKeyRef.current = partnerRecordIdByKey
  const logoErrorsRef = useRef(logoErrors)
  logoErrorsRef.current = logoErrors

  useEffect(() => {
    if (hydratedFromServer.current) return
    if (isLoading) return

    hydratedFromServer.current = true

    const { nextChecked, nextValues, nextIds, nextLogoUrls, saved } = hydratePartnerMapsFromList(
      initialPartners,
      categoryKeySet,
    )

    setCheckedRows(prev => ({ ...prev, ...nextChecked }))
    setPartnerValues(prev => ({ ...prev, ...nextValues }))
    setPartnerRecordIdByKey(prev => ({ ...prev, ...nextIds }))
    setPartnerLogoUrlByKey(prev => ({ ...prev, ...nextLogoUrls }))
    setAccordionBodyMounted(prev => {
      const next = { ...prev }
      Object.keys(nextIds).forEach(k => {
        next[k] = true
      })
      return next
    })
    savedSnapshotByKeyRef.current = { ...savedSnapshotByKeyRef.current, ...saved }
    setFormUiReady(true)
  }, [categoryKeySet, initialPartners, isLoading])

  const handleCheckedChange = useCallback((id: string, checked: boolean) => {
    setCheckedRows(prev => ({ ...prev, [id]: checked }))
    if (checked) {
      setAccordionBodyMounted(prev => ({ ...prev, [id]: true }))
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setOnlyAccordionOpen(id)
        })
      })
    } else {
      setOpenAccordions(prev => ({ ...prev, [id]: false }))
    }
  }, [])

  const handleAccordionTransitionEnd = useCallback((e: React.TransitionEvent<HTMLDivElement>, key: string) => {
    if (e.target !== e.currentTarget) return
    if (e.propertyName !== "grid-template-rows") return
    if (checkedRowsRef.current[key]) return
    setAccordionBodyMounted(prev => {
      if (!prev[key]) return prev
      return { ...prev, [key]: false }
    })
  }, [])

  const toggleAccordion = useCallback(
    (key: string) => {
      setOpenAccordions(prev => {
        if (prev[key]) {
          return { ...prev, [key]: false }
        }
        const next: Record<string, boolean> = {}
        visiblePartnerKeys.forEach(k => {
          next[k] = false
        })
        next[key] = true
        return next
      })
    },
    [visiblePartnerKeys],
  )

  const revealAccordionRows = useCallback((keys: string[]) => {
    const patch = Object.fromEntries(keys.map((k): [string, boolean] => [k, true]))
    setAccordionBodyMounted(prev => ({ ...prev, ...patch }))
    setOpenAccordions(prev => ({ ...prev, ...patch }))
  }, [])

  const updateField = useCallback(<K extends keyof PartnerValues>(id: string, field: K, value: PartnerValues[K]) => {
    setPartnerValues(prev => ({
      ...prev,
      [id]: { ...(prev[id] ?? defaultPartnerValues()), [field]: value },
    }))
  }, [])

  const validatePhoneFieldOnBlur = useCallback((id: string, field: "phone" | "phoneForTexts") => {
    const values = partnerValuesRef.current[id] ?? emptyPartnerRowTemplate
    const { phone } = computePartnerFormErrors(values)
    setPhoneErrors(prev => ({
      ...prev,
      [id]: mergeFieldErrorsForBlur(prev[id] ?? NO_PHONE_ERRORS, phone, field),
    }))
  }, [])

  const validateUrlFieldOnBlur = useCallback(
    (id: string, websiteUrl: string, scheduleUrl: string, blurred: "websiteUrl" | "scheduleUrl") => {
      const base = partnerValuesRef.current[id] ?? emptyPartnerRowTemplate
      const { url } = computePartnerFormErrors({ ...base, websiteUrl, scheduleUrl })
      setUrlErrors(prev => ({
        ...prev,
        [id]: mergeFieldErrorsForBlur(prev[id] ?? NO_URL_ERRORS, url, blurred),
      }))
    },
    [],
  )

  const validateAllCheckedPartners = useCallback((): { ok: boolean; orderedErrorKeys: string[] } => {
    const nextPhoneErrors: Record<string, PhoneErrors> = {}
    const nextUrlErrors: Record<string, UrlErrors> = {}
    const orderedErrorKeys: string[] = []

    visiblePartnerKeys.forEach(key => {
      if (!checkedRowsRef.current[key]) return

      const values = partnerValuesRef.current[key] ?? emptyPartnerRowTemplate
      const { phone: pErr, url: uErr } = computePartnerFormErrors(values)
      nextPhoneErrors[key] = pErr
      nextUrlErrors[key] = uErr

      const le = logoErrorsRef.current[key]
      const hasLogoErr = !!(le && String(le).trim() !== "")
      if (phoneErrorsNonEmpty(pErr) || urlErrorsNonEmpty(uErr) || hasLogoErr) {
        orderedErrorKeys.push(key)
      }
    })

    setPhoneErrors(prev => ({ ...prev, ...nextPhoneErrors }))
    setUrlErrors(prev => ({ ...prev, ...nextUrlErrors }))

    return { ok: orderedErrorKeys.length === 0, orderedErrorKeys }
  }, [visiblePartnerKeys])

  const scrollToPartnerRow = (key: string) => {
    window.setTimeout(() => {
      scrollSectionRef.current?.querySelector<HTMLElement>(`#partner-row-${key}`)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
    }, 400)
  }

  const validatePartners = useCallback((): boolean => {
    const checkedKeys = visiblePartnerKeys.filter(k => checkedRowsRef.current[k])
    if (checkedKeys.length === 0) return true
    const { ok, orderedErrorKeys } = validateAllCheckedPartners()
    if (!ok) {
      revealAccordionRows(orderedErrorKeys)
      const firstErrorKey = orderedErrorKeys[0]
      if (firstErrorKey) scrollToPartnerRow(firstErrorKey)
    }
    return ok
  }, [revealAccordionRows, validateAllCheckedPartners, visiblePartnerKeys])

  const hasChanges = useCallback(() => {
    if (!formUiReady) return false

    return visiblePartnerKeys.some(key => {
      const isChecked = checkedRowsRef.current[key]
      const hasSavedRecord = partnerRecordIdByKeyRef.current[key] != null

      if (isChecked && !hasSavedRecord) return true
      if (!isChecked && hasSavedRecord) return true

      return (
        isChecked &&
        isPartnerDirty(key, partnerValuesRef.current[key] ?? emptyPartnerRowTemplate, savedSnapshotByKeyRef.current)
      )
    })
  }, [formUiReady, visiblePartnerKeys])

  useEffect(() => {
    onDirtyChange?.(hasChanges())
  }, [hasChanges, onDirtyChange])

  const persistPartnersWithApi = useCallback(
    async (activeApi: ContractorPartnersFormApi): Promise<PartnerPersistResult> => {
      const checkedKeys = visiblePartnerKeys.filter(k => checkedRowsRef.current[k])
      const keysToDelete = visiblePartnerKeys.filter(
        k => !checkedRowsRef.current[k] && partnerRecordIdByKeyRef.current[k] != null,
      )

      if (checkedKeys.length > 0) {
        const { ok, orderedErrorKeys } = validateAllCheckedPartners()
        if (!ok) {
          revealAccordionRows(orderedErrorKeys)
          const firstErrorKey = orderedErrorKeys[0]
          if (firstErrorKey) scrollToPartnerRow(firstErrorKey)
          return { ok: false, reason: "validation" }
        }
      }

      const keysToCreate = checkedKeys.filter(k => partnerRecordIdByKeyRef.current[k] == null)
      const keysToUpdate = checkedKeys.filter(k => {
        const id = partnerRecordIdByKeyRef.current[k]
        if (id == null) return false
        return isPartnerDirty(k, partnerValuesRef.current[k] ?? emptyPartnerRowTemplate, savedSnapshotByKeyRef.current)
      })

      if (keysToDelete.length === 0 && keysToCreate.length === 0 && keysToUpdate.length === 0) {
        if (!silentPartnerToasts) {
          toast.info(noChanges)
        }
        return { ok: true }
      }

      try {
        let nextRecordIds = { ...partnerRecordIdByKeyRef.current }

        const deleteTargets = keysToDelete
          .map(key => {
            const id = nextRecordIds[key]
            return id != null ? { key, id } : null
          })
          .filter((x): x is { key: string; id: number } => x != null)

        await Promise.all(deleteTargets.map(({ id }) => activeApi.deletePartner(id)))

        for (const { key } of deleteTargets) {
          delete nextRecordIds[key]
          delete savedSnapshotByKeyRef.current[key]
        }

        if (deleteTargets.length > 0) {
          setPartnerLogoUrlByKey(prev => {
            const n = { ...prev }
            deleteTargets.forEach(({ key }) => {
              delete n[key]
            })
            return n
          })
        }

        const updatePromises = keysToUpdate.map(async key => {
          const id = nextRecordIds[key]
          if (id == null) return null
          const category = categoryByPartnerKey.get(key)
          if (!category) return null
          const values = partnerValuesRef.current[key] ?? emptyPartnerRowTemplate
          const body = buildPartnerFormData(category.id, values, { sendLogoFieldAlways: true })
          const res = await activeApi.updatePartner(id, body)
          return { key, res, values } as const
        })

        const createPromises = keysToCreate.map(async key => {
          const category = categoryByPartnerKey.get(key)
          if (!category) return null
          const values = partnerValuesRef.current[key] ?? emptyPartnerRowTemplate
          const body = buildPartnerFormData(category.id, values, {})
          const res = await activeApi.createPartner(body)
          return { key, res, values } as const
        })

        const [updateResults, createResults] = await Promise.all([
          Promise.all(updatePromises),
          Promise.all(createPromises),
        ])

        const idAssignKeys = new Set(keysToCreate)
        const { clearedByKey, logoUrlPatch, newIds, snapshots } = foldPartnerMutationResults(
          [...updateResults, ...createResults],
          idAssignKeys,
        )
        Object.assign(nextRecordIds, newIds)
        Object.assign(savedSnapshotByKeyRef.current, snapshots)

        if (Object.keys(clearedByKey).length > 0) {
          setPartnerValues(prev => {
            const next = { ...prev }
            for (const [k, v] of Object.entries(clearedByKey)) {
              next[k] = v
            }
            return next
          })
        }

        if (Object.keys(logoUrlPatch).length > 0) {
          setPartnerLogoUrlByKey(prev => {
            const next = { ...prev }
            for (const [k, url] of Object.entries(logoUrlPatch)) {
              if (url) next[k] = url
              else delete next[k]
            }
            return next
          })
        }

        setPartnerRecordIdByKey(nextRecordIds)
        await Promise.resolve(activeApi.onPartnerChangesSettled?.())
        if (!silentPartnerToasts) {
          toast.success(saveSuccess)
        }
        return { ok: true }
      } catch (error) {
        toast.error(getErrorMessage(error as Parameters<typeof getErrorMessage>[0]) || saveError)
        return { ok: false, reason: "error", error }
      }
    },
    [
      categoryByPartnerKey,
      noChanges,
      revealAccordionRows,
      saveError,
      saveSuccess,
      silentPartnerToasts,
      validateAllCheckedPartners,
      visiblePartnerKeys,
    ],
  )

  const savePartners = useCallback(
    async (contractorId?: number): Promise<PartnerPersistResult> => {
      if (resolvePartnerApi) {
        if (contractorId == null) {
          const err = new Error("contractorId is required for vendor partner save")
          toast.error(getErrorMessage(err) || saveError)
          return { ok: false, reason: "error", error: err }
        }
        return persistPartnersWithApi(resolvePartnerApi(contractorId))
      }
      if (!api) {
        const err = new Error("ContractorPartnersForm: api is required when resolvePartnerApi is not set")
        toast.error(saveError)
        return { ok: false, reason: "error", error: err }
      }
      return persistPartnersWithApi(api)
    },
    [api, persistPartnersWithApi, resolvePartnerApi, saveError],
  )

  useImperativeHandle(ref, () => ({ validatePartners, hasChanges, savePartners }), [
    hasChanges,
    savePartners,
    validatePartners,
  ])

  const handleSave = async () => {
    if (!api && !resolvePartnerApi) return
    await savePartners()
  }

  const clearPhoneErrorsFor = (key: string) =>
    setPhoneErrors(prev => (phoneErrorsNonEmpty(prev[key] ?? NO_PHONE_ERRORS) ? { ...prev, [key]: {} } : prev))
  const clearUrlErrorsFor = (key: string) =>
    setUrlErrors(prev => (urlErrorsNonEmpty(prev[key] ?? NO_URL_ERRORS) ? { ...prev, [key]: {} } : prev))

  const handlePhoneInput = useCallback(
    (key: string, val: string) => {
      clearPhoneErrorsFor(key)
      updateField(key, "phone", val)
    },
    [updateField],
  )

  const handlePhoneForTextsInput = useCallback(
    (key: string, val: string) => {
      clearPhoneErrorsFor(key)
      updateField(key, "phoneForTexts", val)
    },
    [updateField],
  )

  const handleUseForTextsChange = useCallback((key: string, checked: boolean) => {
    clearPhoneErrorsFor(key)
    setPartnerValues(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] ?? defaultPartnerValues()),
        useForTexts: checked,
        ...(checked ? { phoneForTexts: "" } : {}),
      },
    }))
  }, [])

  const handleWebsiteUrlInput = useCallback(
    (key: string, value: string) => {
      clearUrlErrorsFor(key)
      updateField(key, "websiteUrl", value)
    },
    [updateField],
  )

  const handleScheduleUrlInput = useCallback(
    (key: string, value: string) => {
      clearUrlErrorsFor(key)
      updateField(key, "scheduleUrl", value)
    },
    [updateField],
  )

  const handleLogoSetFile = useCallback(
    (key: string, files: FileList | null) => {
      setLogoErrors(prev => (prev[key] ? { ...prev, [key]: "" } : prev))
      updateField(key, "logo", files?.[0] ?? null)
    },
    [updateField],
  )

  const handleLogoValidationError = useCallback(
    (key: string, errorMessage: string) => {
      setLogoErrors(prev => ({ ...prev, [key]: errorMessage }))
      updateField(key, "logo", null)
    },
    [updateField],
  )

  const onUrlBlur = useCallback(
    (id: string, field: "websiteUrl" | "scheduleUrl") => (e: FormEvent<HTMLInputElement>) => {
      const raw = e.currentTarget.value.trim()
      const values = partnerValuesRef.current[id] ?? emptyPartnerRowTemplate
      let valueToValidate = raw
      const normalized = withHttpsIfSchemeless(raw)
      if (normalized != null) {
        updateField(id, field, normalized)
        valueToValidate = normalized
      }
      const websiteUrl = field === "websiteUrl" ? valueToValidate : values.websiteUrl
      const scheduleUrl = field === "scheduleUrl" ? valueToValidate : values.scheduleUrl
      validateUrlFieldOnBlur(id, websiteUrl, scheduleUrl, field)
    },
    [updateField, validateUrlFieldOnBlur],
  )

  const showLoading = isLoading || !formUiReady
  const showFooter = variant === "standalone"
  const isEmbedded = variant === "embedded"

  return (
    <PageRoot $embedded={isEmbedded} data-testid="partners-form">
      <ScrollSection ref={scrollSectionRef} $embedded={isEmbedded}>
        {!isEmbedded ? <Title>{title}</Title> : null}
        {showLoading ? (
          <SpinnerWrap data-testid="partners-form-loading">
            <Spinner />
          </SpinnerWrap>
        ) : (
          <Wrapper data-testid="partners-form-rows">
            {visibleCategories.map(({ id, name }) => {
              const key = String(id)
              const isChecked = checkedRows[key] ?? false
              const showAccordionBody = accordionBodyMounted[key] ?? false
              const isAccordionOpen = !!openAccordions[key]
              const values = partnerValues[key] ?? emptyPartnerRowTemplate

              return (
                <PartnerCategoryRow
                  key={key}
                  rowKey={key}
                  categoryName={name}
                  isChecked={isChecked}
                  showAccordionBody={showAccordionBody}
                  isAccordionOpen={isAccordionOpen}
                  values={values}
                  phoneErrors={phoneErrors[key] ?? NO_PHONE_ERRORS}
                  urlErrors={urlErrors[key] ?? NO_URL_ERRORS}
                  logoError={logoErrors[key]}
                  logoImageUrl={partnerLogoUrlByKey[key]}
                  onCheckedChange={handleCheckedChange}
                  onToggleAccordion={toggleAccordion}
                  onAccordionTransitionEnd={handleAccordionTransitionEnd}
                  onPhoneInput={handlePhoneInput}
                  onPhoneForTextsInput={handlePhoneForTextsInput}
                  onUseForTextsChange={handleUseForTextsChange}
                  onWebsiteUrlInput={handleWebsiteUrlInput}
                  onScheduleUrlInput={handleScheduleUrlInput}
                  onPhoneBlur={validatePhoneFieldOnBlur}
                  onUrlBlur={onUrlBlur}
                  onLogoSetFile={handleLogoSetFile}
                  onLogoValidationError={handleLogoValidationError}
                />
              )
            })}
          </Wrapper>
        )}
      </ScrollSection>
      {showFooter ? (
        <Footer data-testid="partners-save">
          <ButtonNew color="blue" text={isSaving ? "Saving..." : "Save"} onClick={handleSave} disabled={isSaving} />
        </Footer>
      ) : null}
    </PageRoot>
  )
}
